import { useCallback } from "react"

import { useFieldArray, useFormContext } from "react-hook-form"

import styles from "./CompanySmsSendingRuleRowEditV2.module.scss"
import { ConversionSelect, toConversionOptionValue } from "./ConversionSelect"
import { UnitsEdit } from "./UnitsEdit"
import { useUnitsEdit } from "./useUnitsEdit"

import { getManuallyClaimingUnitSegmentColor } from "@/souhl_admin/components/features/companies/sms-sending-rule/EncouragingRereservationUnitsSlider/EncouragingRereservationUnitsSlider"
import type { CompanySmsSendingRuleV2EditType } from "@/souhl_admin/utils/form/companySmsSendingRuleV2Edit"

import type { ManuallyClaimingConversion } from "./ConversionSelect"
import type { FieldErrors } from "react-hook-form"

type Props = {
	index: number
	// 手動請求区間 [rangeStart, rangeEnd)
	rangeStart: number
	rangeEnd: number
	// 手動請求区間があるか。無い間も unmount せず mount し続ける (区間追従の effect で unit を全削除させるため)
	isRangeEnabled: boolean
	errors: FieldErrors<CompanySmsSendingRuleV2EditType>
}

// 手動請求区間を来店回数で unit に分け、unit ごとに「何に転換するか × 転換日数」を設定する (詳細設定からのみ設定できる)。
// unit が 0 件なら転換しない普通の手動請求。区間追従・スライダー・区間の追加/削除は再予約案内 unit と同じ挙動 (useUnitsEdit / UnitsEdit で共通)
export const ManuallyClaimingUnitsEdit = ({
	index,
	rangeStart,
	rangeEnd,
	isRangeEnabled,
	errors,
}: Props) => {
	const { control, setValue, watch } =
		useFormContext<CompanySmsSendingRuleV2EditType>()
	const rowErrors = errors?.cancelTypeSettingInputs?.[index] ?? {}
	const unitsPath =
		`cancelTypeSettingInputs.${index}.manuallyClaimingUnits` as const
	const { fields: unitFields, replace: replaceUnits } = useFieldArray({
		control,
		name: unitsPath,
	})
	// NOTE: watch が返す配列は setValue で in-place に書き換わり参照が変わらないため、unit 由来の値は毎 render 計算する
	const units = watch(unitsPath) ?? []

	const setUnitMinVisitCount = useCallback(
		(
			unitIndex: number,
			value: number,
			options: { shouldValidate: boolean }
		) => {
			setValue(
				`${unitsPath}.${unitIndex}.minVisitCount` as const,
				value,
				options
			)
		},
		[unitsPath, setValue]
	)

	const unitsEdit = useUnitsEdit({
		units,
		replaceUnits,
		setUnitMinVisitCount,
		rangeStart,
		rangeEnd,
		// 詳細設定を開いた時は未設定 "-" (転換しない) で作る
		createInitialUnit: () => ({
			minVisitCount: rangeStart,
			manuallyClaimingUnitKind: null,
			conversionDays: null,
		}),
		createSplitUnit: (source, minVisitCount) => ({
			minVisitCount,
			manuallyClaimingUnitKind: source.manuallyClaimingUnitKind ?? null,
			conversionDays: source.conversionDays ?? null,
		}),
	})

	const handleUnitConversionChange = (
		unitIndex: number,
		conversion: ManuallyClaimingConversion
	) => {
		setValue(
			`${unitsPath}.${unitIndex}.manuallyClaimingUnitKind` as const,
			conversion.manuallyClaimingUnitKind,
			{ shouldValidate: true }
		)
		setValue(
			`${unitsPath}.${unitIndex}.conversionDays` as const,
			conversion.conversionDays,
			{ shouldValidate: true }
		)
	}

	// 区間が無い間は「-」だけ出す。区間追従の effect が走って unit は空になっているので、古い unit が yup に引っかかって
	// 見えない所でエラーになることは無い (hooks の順序を保つため early return は全 hooks の後に置く)
	if (!isRangeEnabled) {
		return <div className={styles.disabled_cell}>-</div>
	}

	return (
		<UnitsEdit
			rangeStart={rangeStart}
			rangeEnd={rangeEnd}
			unitsEdit={unitsEdit}
			unitKeys={unitFields.map((field) => field.id)}
			detailNote="手動請求の区間を来店回数で分け、区間ごとに転換するかを選べます"
			idleNote="未請求のまま一定日数たったキャンセルを再予約案内に切り替えたい場合は詳細設定を押してください"
			startFixedLabel="手動請求の開始"
			getUnitColor={getManuallyClaimingUnitSegmentColor}
			renderUnitControl={(unitIndex) => (
				<ConversionSelect
					value={toConversionOptionValue({
						manuallyClaimingUnitKind:
							units[unitIndex]?.manuallyClaimingUnitKind ?? null,
						conversionDays: units[unitIndex]?.conversionDays ?? null,
					})}
					onChange={(conversion) =>
						handleUnitConversionChange(unitIndex, conversion)
					}
				/>
			)}
			getUnitErrorMessage={(unitIndex) =>
				rowErrors?.manuallyClaimingUnits?.[unitIndex]?.conversionDays?.message
			}
			rootErrorMessage={
				rowErrors?.manuallyClaimingUnits?.root?.message ||
				rowErrors?.manuallyClaimingUnits?.message
			}
		/>
	)
}
